import * as actions from './actions';

import { ActionType, createReducer } from 'typesafe-actions';

import { ACTION_STATUSES } from 'shared/consts';
import {
  formDefinitionKey,
  BookingAddOnFormState,
  BookingAddOnRangesState,
  BookingAddOnsMenuState,
  BookingAddOnsState,
} from './types';

const NO_SUBMIT: BookingAddOnsState['submitBookingAddOns'] = { status: null, error: null };

/*
 * ⭐ `isValid: true` is the right default, not `false`. The submit gate ANDs this in, and most bookings
 * carry no vendor form at all — defaulting false would block every form-less order behind a validity
 * signal that is never going to arrive.
 */
const NO_FORM: BookingAddOnFormState = {
  status: null,
  templateUuid: null,
  definition: null,
  values: {},
  isValid: true,
  unresolvedItemIds: [],
  pendingKey: null,
};

const NO_RANGES: BookingAddOnRangesState = { status: null, byCatalogItemUuid: {}, pendingKey: null };

const NO_MENU: BookingAddOnsMenuState = { key: null, resource: null, status: null };

export const initialState: BookingAddOnsState = {
  bookingId: null,
  addOns: [],
  quantityViolations: [],
  submitBookingAddOns: NO_SUBMIT,
  form: NO_FORM,
  ranges: NO_RANGES,
  menu: NO_MENU,
  formDefinitions: {},
  amendingFrom: null,
  lastOrdered: null,
  lastCancelled: null,
  seededFor: null,
};

/*
 * Claiming the slice for a different booking DROPS the previous selection, and its submit state with it.
 * This is the guard against a selection made on one booking being submitted against another — see the note
 * on `BookingAddOnsState.bookingId`. Re-claiming for the SAME booking is a no-op, so remounting the step
 * does not wipe what the tenant has chosen.
 */
const startBookingAddOnsHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.startBookingAddOns>,
): BookingAddOnsState =>
  state.bookingId === payload
    ? state
    : /*
       * ⛔ A FRESH OBJECT LITERAL, not a spread — so every field must be named here explicitly.
       * `form` included: a new booking must not inherit the previous one's answers, which would file
       * one tenant's catering questions against another's request. `tsc` catches an omission because
       * of the return annotation, and `check_types` runs on every PR.
       */
      /* `menu` is kept: the step claims the slice only after its read lands, and re-reads on every mount. */
      /*
       * `ranges` is DROPPED rather than kept, unlike `menu`. A different booking is a different room
       * and so a different menu, and a range carried over would bound a control against a product the
       * tenant is not looking at. An absent range costs the bound; a wrong one costs the order.
       */
      {
        bookingId: payload,
        addOns: [],
        quantityViolations: [],
        submitBookingAddOns: NO_SUBMIT,
        form: NO_FORM,
        ranges: NO_RANGES,
        menu: state.menu,
        formDefinitions: state.formDefinitions,
        /* Carried like `formDefinitions`: these name their own booking, so claiming a different one
         * must not disarm the card of the booking just ordered. */
        amendingFrom: state.amendingFrom,
        lastOrdered: state.lastOrdered,
        lastCancelled: state.lastCancelled,
        /* A different booking has never been seeded. */
        seededFor: null,
      };

/*
 * Guarded twice: the booking id must match, because the seed is built asynchronously off the menu read
 * and the tenant can leave meanwhile; and `seededFor` must not already name this booking, because a
 * seed is an offer, not a correction — re-applying it undoes lines the tenant has since removed.
 */
const seedBookingAddOnsHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.seedBookingAddOns>,
): BookingAddOnsState =>
  state.bookingId !== payload.bookingId || state.seededFor === payload.bookingId
    ? state
    : {
        ...state,
        addOns: payload.addOns,
        /* Unnormalised, like every other write to this field; the strip belongs at the wire edge in
         * `buildFormResponse`. Absent answers leave the form alone rather than emptying it. */
        form: payload.formValues
          ? { ...state.form, templateUuid: payload.formTemplateUuid, values: payload.formValues }
          : state.form,
        seededFor: payload.bookingId,
      };

/*
 * Only the edited line's refusal is stale. Clearing all of them would hide a live one and re-enable
 * the control, so the next press refuses again and the notice flickers.
 */
const withoutViolationFor = (state: BookingAddOnsState, addOnId: string) =>
  state.quantityViolations.filter(violation => violation.id !== addOnId);

const addBookingAddOnHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.addBookingAddOn>,
): BookingAddOnsState => {
  const indexOfAddOn = state.addOns.findIndex(addOn => addOn.id === payload.id);

  if (indexOfAddOn >= 0) {
    const addOnsCopy = [...state.addOns];
    addOnsCopy[indexOfAddOn] = payload;

    return { ...state, addOns: addOnsCopy, quantityViolations: withoutViolationFor(state, payload.id) };
  }

  return { ...state, addOns: [...state.addOns, payload], quantityViolations: withoutViolationFor(state, payload.id) };
};

const removeBookingAddOnHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.removeBookingAddOn>,
): BookingAddOnsState => ({
  ...state,
  addOns: state.addOns.filter(addOn => addOn.id !== payload),
  quantityViolations: withoutViolationFor(state, payload),
});

/*
 * Keyed by template AND language, not by booking, so it outlives a claim and a reset. The language
 * belongs in the key: labels are localised per fetch, so keying on the template alone would keep
 * serving the previous locale's questions after the tenant switches.
 */
const readFormDefinitionSuccessHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.readAddOnFormDefinition.success>,
): BookingAddOnsState => ({
  ...state,
  formDefinitions: {
    ...state.formDefinitions,
    [formDefinitionKey(payload.templateUuid, payload.lang)]: payload.definition,
  },
});

/* `lastOrdered` and `lastCancelled` survive the reset: both are dispatched from the submit-success
 * handler, and clearing them here would make the order of those dispatches load-bearing and silent.
 * `amendingFrom` survives for the opposite reason -- it is stamped BEFORE the picker opens, and this
 * reset runs as it does, so clearing it here would throw the baseline away on the way in. */
const resetBookingAddOnsHandler = (state: BookingAddOnsState): BookingAddOnsState => ({
  ...initialState,
  formDefinitions: state.formDefinitions,
  amendingFrom: state.amendingFrom,
  lastOrdered: state.lastOrdered,
  lastCancelled: state.lastCancelled,
});

const resolveRangesRequestHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.resolveAddOnRanges.request>,
): BookingAddOnsState => ({
  ...state,
  /*
   * The previous ranges stay in place while the next read runs. Blanking them would briefly unbound
   * every stepper, which is the one state this feature exists to prevent.
   *
   * ⛔ Note what this handler does NOT touch: `form`. The status written here must never reach
   * `isResolvingForm`, which gates the Continue button — that is the whole reason this lane has its
   * own branch instead of borrowing the form's.
   */
  ranges: { ...state.ranges, status: ACTION_STATUSES.PENDING, pendingKey: payload.requestKey },
});

/*
 * ⭐ RAISE A LINE TO ITS MINIMUM, NEVER LOWER ONE TO ITS MAXIMUM. The asymmetry is the whole point,
 * and it is why this is a named function rather than an inline map.
 *
 * The range read is asynchronous, so a tenant can tap Add before it lands and store a quantity of one
 * against a product whose floor is three. Acceptance criterion 3 says adding that product puts three
 * in the order, and leaving the race uncorrected breaks it — work-orders then refuses the whole
 * selection over a quantity the tenant never chose.
 *
 * ⛔ Lowering is the opposite case and must not happen. A tenant who deliberately chose five of
 * something whose maximum has since dropped to two still owns that choice. RES-1333 refuses that
 * basket at submit and names the bound; the correction is theirs to make, so rewriting the quantity
 * here would still take it out of their hands.
 *
 * An unresolved range yields a floor of one, which no selected line is ever below, so it is a no-op.
 */
const raiseLinesToTheirMinimum = (
  addOns: BookingAddOnsState['addOns'],
  ranges: ActionType<typeof actions.resolveAddOnRanges.success>['payload']['ranges'],
): BookingAddOnsState['addOns'] =>
  addOns.map(line => {
    const floor = Math.max(1, ranges[line.id.toLowerCase()]?.minQuantity ?? 1);

    return line.quantity < floor ? { ...line, quantity: floor } : line;
  });

const resolveRangesSuccessHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.resolveAddOnRanges.success>,
): BookingAddOnsState => {
  /*
   * Stale-guarded against its OWN key, never the form's. Same two escapes as the form lane: the tenant
   * leaves the step with a read in flight, or the slice is claimed for a different booking.
   */
  if (payload.bookingId !== state.bookingId || payload.requestKey !== state.ranges.pendingKey) {
    return state;
  }

  return {
    ...state,
    addOns: raiseLinesToTheirMinimum(state.addOns, payload.ranges),
    ranges: { status: ACTION_STATUSES.FULFILLED, byCatalogItemUuid: payload.ranges, pendingKey: null },
  };
};

/*
 * ⚠️ Clears the map rather than keeping what was there. A failed read means we do not know the bounds,
 * and the consumer reads "no range" as unbounded-with-a-floor-of-one — today's behaviour. Keeping a
 * previous map would instead assert bounds nothing has confirmed for the current menu.
 */
const resolveRangesFailureHandler = (state: BookingAddOnsState): BookingAddOnsState => ({
  ...state,
  ranges: { status: ACTION_STATUSES.REJECTED, byCatalogItemUuid: {}, pendingKey: null },
});

const resolveFormRequestHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.resolveAddOnForm.request>,
): BookingAddOnsState => ({
  ...state,
  /*
   * The previous form stays visible while the next one resolves, deliberately: re-resolving fires on
   * every change of the item set, and blanking the screen on each would flicker a form the tenant is
   * part-way through. What must NOT happen is acting on it — the CTA is blocked while `status` is
   * pending, which is what stops a stale form being answered or a submission going out without one.
   */
  form: { ...state.form, status: ACTION_STATUSES.PENDING, pendingKey: payload.requestKey },
});

/*
 * ⚠️ The resolved TEMPLATE is what invalidates the answers, not the party. If the tenant goes back,
 * clears one vendor and picks another, the answers they already gave were given against the first
 * vendor's questions — the keys are FIELD KEYS, so carrying them over would reinterpret them against
 * a different question set and file them on the wrong company's request. Same template, keep them.
 */
/*
 * ⛔ A RESULT NOTHING IS WAITING FOR. `switchMap` cancels an in-flight read only when a NEW one is
 * issued, and two paths leave one running with no successor: the tenant empties their selection, and
 * the slice is claimed for a different booking. Either way the late response would otherwise be
 * written as current — at worst putting one booking's vendor questions on another's.
 */
const isStaleResult = (
  state: BookingAddOnsState,
  payload: ActionType<typeof actions.resolveAddOnForm.success>['payload'],
): boolean => payload.bookingId !== state.bookingId || payload.requestKey !== state.form.pendingKey;

const resolveFormSuccessHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.resolveAddOnForm.success>,
): BookingAddOnsState => {
  if (isStaleResult(state, payload)) {
    return state;
  }

  const isSameTemplate = state.form.templateUuid === payload.templateUuid;

  return {
    ...state,
    form: {
      status: ACTION_STATUSES.FULFILLED,
      templateUuid: payload.templateUuid,
      definition: payload.definition,
      values: isSameTemplate ? state.form.values : {},
      isValid: isSameTemplate ? state.form.isValid : true,
      unresolvedItemIds: payload.unresolvedItemIds,
      pendingKey: null,
    },
  };
};

/*
 * The selection no longer names anything a form could apply to. `pendingKey` goes to null with it,
 * so a resolve still in flight cannot repopulate what was just cleared.
 */
const clearFormHandler = (state: BookingAddOnsState): BookingAddOnsState => ({ ...state, form: NO_FORM });

/*
 * ⛔ FAILS OPEN. The form is cleared and `isValid` returns to true, so the step behaves exactly as it
 * does for the ~majority of bookings that have no vendor form. The alternative — blocking submit —
 * would let a forms outage take out add-on ordering entirely for a tenant who has already paid.
 * The cost is named rather than hidden: the vendor receives the order with no answers on it.
 */
const resolveFormFailureHandler = (state: BookingAddOnsState): BookingAddOnsState => ({
  ...state,
  form: { ...NO_FORM, status: ACTION_STATUSES.REJECTED },
});

const setFormValuesHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.setAddOnFormValues>,
): BookingAddOnsState => ({ ...state, form: { ...state.form, values: payload } });

const setFormValidityHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.setAddOnFormValidity>,
): BookingAddOnsState => ({ ...state, form: { ...state.form, isValid: payload } });

const submitRequestHandler = (state: BookingAddOnsState): BookingAddOnsState => ({
  ...state,
  submitBookingAddOns: { status: ACTION_STATUSES.PENDING, error: null },
});

const submitSuccessHandler = (state: BookingAddOnsState): BookingAddOnsState => ({
  ...state,
  submitBookingAddOns: { status: ACTION_STATUSES.FULFILLED, error: null },
});

const submitFailureHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.submitBookingAddOns.failure>,
): BookingAddOnsState => ({
  ...state,
  submitBookingAddOns: { status: ACTION_STATUSES.REJECTED, error: payload?.error },
});

/*
 * Writes the fresh bounds so the steppers the tenant corrects with are the ones just read, and does
 * NOT raise an under-min line: at submit that raise would change a quantity in the tick it is sent in.
 */
const blockAddOnSubmissionHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.blockAddOnSubmission>,
): BookingAddOnsState =>
  payload.bookingId !== state.bookingId
    ? state
    : {
        ...state,
        quantityViolations: payload.violations,
        submitBookingAddOns: NO_SUBMIT,
        ranges: {
          status: ACTION_STATUSES.FULFILLED,
          /* Merged, not replaced: a batch that timed out contributes nothing, and replacing would
           * drop the bounds those products already had. */
          byCatalogItemUuid: { ...state.ranges.byCatalogItemUuid, ...payload.ranges },
          /* The in-flight menu read's key is kept: nulling it makes the stale-guard drop that answer,
           * and the items it would have bounded keep none. */
          pendingKey: state.ranges.pendingKey,
        },
      };

const resetSubmitStatusHandler = (state: BookingAddOnsState): BookingAddOnsState => ({
  ...state,
  submitBookingAddOns: NO_SUBMIT,
});

const markOrderedHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.markAddOnsOrdered>,
): BookingAddOnsState => ({ ...state, lastOrdered: payload });

const markAmendingHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.markAddOnsAmending>,
): BookingAddOnsState => ({ ...state, amendingFrom: payload });

const markCancelledHandler = (
  state: BookingAddOnsState,
  { payload }: ActionType<typeof actions.markAddOnsCancelled>,
): BookingAddOnsState => ({ ...state, lastCancelled: payload });

const bookingAddOnsReducer = createReducer(initialState)
  .handleAction(actions.startBookingAddOns, startBookingAddOnsHandler)
  .handleAction(actions.seedBookingAddOns, seedBookingAddOnsHandler)
  .handleAction(actions.addBookingAddOn, addBookingAddOnHandler)
  .handleAction(actions.removeBookingAddOn, removeBookingAddOnHandler)
  .handleAction(actions.resetBookingAddOns, resetBookingAddOnsHandler)
  .handleAction(actions.submitBookingAddOns.request, submitRequestHandler)
  .handleAction(actions.submitBookingAddOns.success, submitSuccessHandler)
  .handleAction(actions.submitBookingAddOns.failure, submitFailureHandler)
  .handleAction(actions.resetSubmitBookingAddOnsStatus, resetSubmitStatusHandler)
  .handleAction(actions.blockAddOnSubmission, blockAddOnSubmissionHandler)
  .handleAction(actions.markAddOnsAmending, markAmendingHandler)
  .handleAction(actions.markAddOnsOrdered, markOrderedHandler)
  .handleAction(actions.markAddOnsCancelled, markCancelledHandler)
  .handleAction(actions.resolveAddOnRanges.request, resolveRangesRequestHandler)
  .handleAction(actions.resolveAddOnRanges.success, resolveRangesSuccessHandler)
  .handleAction(actions.resolveAddOnRanges.failure, resolveRangesFailureHandler)
  .handleAction(actions.resolveAddOnForm.request, resolveFormRequestHandler)
  .handleAction(actions.resolveAddOnForm.success, resolveFormSuccessHandler)
  .handleAction(actions.resolveAddOnForm.failure, resolveFormFailureHandler)
  .handleAction(actions.readAddOnFormDefinition.success, readFormDefinitionSuccessHandler)
  .handleAction(actions.setAddOnFormValues, setFormValuesHandler)
  .handleAction(actions.setAddOnFormValidity, setFormValidityHandler)
  .handleAction(actions.clearAddOnForm, clearFormHandler)
  .handleAction(actions.getBookingAddOnsMenu.request, (state, { payload }) => ({
    ...state,
    menu: { key: payload.key, resource: null, status: ACTION_STATUSES.PENDING },
  }))
  .handleAction(actions.getBookingAddOnsMenu.success, (state, { payload }) =>
    state.menu.key === payload.key
      ? { ...state, menu: { key: payload.key, resource: payload.resource, status: ACTION_STATUSES.FULFILLED } }
      : state,
  )
  .handleAction(actions.getBookingAddOnsMenu.failure, (state, { payload }) =>
    state.menu.key === payload.key ? { ...state, menu: { ...state.menu, status: ACTION_STATUSES.REJECTED } } : state,
  );

export default bookingAddOnsReducer;
